import React from "react";
import { Drawer as AntDrawer } from "antd";
import PropTypes from "kf-proptypes/widgets";

import { useTheme } from "base/themes";

import {
  KfIcon,
  KF_ICONS,
  ICON_STROKE
} from "@widgets/common/components/primitives/kf_icon/index.jsx";

import "antd/es/drawer/style/css";

import styles from "./modal.css";

export function Drawer({
  children,
  title,
  titleRenderer,
  open = false,
  onClose,
  placement = "right",
  className,
  width,
  height,
  extra = null,
  style,
  closable,
  closeIcon,
  size = "default",
  footerRenderer,
  footerStyle,
  getContainer = () => document.body,
  centered = true,
  closeOnOutsideClick = false,
  closeOnEscape = true,
  destroyOnClose = true,
  bodyStyle
}) {
  let { themeClass } = useTheme();
  return (
    <>
      <AntDrawer
        title={title ? title : titleRenderer && titleRenderer()}
        placement={placement}
        onClose={onClose}
        visible={open}
        getContainer={getContainer}
        onCancel={onClose}
        destroyOnClose={destroyOnClose}
        centered={centered}
        className={`${className} ${styles.drawer} ${themeClass}`}
        style={style}
        extra={extra}
        bodyStyle={bodyStyle}
        maskClosable={closeOnOutsideClick}
        maskStyle={{ backgroundColor: "var(--kf-color-overlay-gray)" }}
        keyboard={closeOnEscape}
        closeIcon={
          closeIcon ? (
            closeIcon
          ) : (
            <KfIcon icon={KF_ICONS.CLOSE} stroke={ICON_STROKE.REGULAR} />
          )
        }
        width={width}
        height={height}
        size={size}
        footer={footerRenderer}
        footerStyle={footerStyle}
        closable={closable}
      >
        {children}
      </AntDrawer>
    </>
  );
}

Drawer.propTypes = {
  /** Title Renderer is used to render custom component in title */
  titleRenderer: PropTypes.func,
  /** To Render the children in drawer */
  children: PropTypes.node.isRequired,
  /** Extra actions area at corner. */
  extra: PropTypes.oneOfType([PropTypes.node, PropTypes.element]),
  /** Custom close icon */
  closeIcon: PropTypes.node,
  /** The placement of the Drawer */
  placement: PropTypes.string,
  /** Title Renderer is used to render custom component in footer */
  footerRenderer: PropTypes.func,
  /** mounted node and display window for Drawer */
  getContainer: PropTypes.func,
  /** Whether the Drawer dialog is visible or not */
  open: PropTypes.bool.isRequired,
  /** Drawer will be centered displayed */
  centered: PropTypes.bool,
  /** Style of Drawer panel. Use bodyStyle if want to config body only */
  style: PropTypes.shape({
    display: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  /** Style of the drawer content part */
  bodyStyle: PropTypes.shape({
    display: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  /** To override the default custom behaviour */
  className: PropTypes.string,
  /** Whether a close (x) button is visible on top left of the Drawer dialog or not. */
  closable: PropTypes.bool,
  /** Whether to unmount child components on closing drawer or not */
  destroyOnClose: PropTypes.bool,
  /** This prop will trigger when user clicks outside component */
  closeOnOutsideClick: PropTypes.bool,
  /** This prop will trigger when user click escape button */
  closeOnEscape: PropTypes.bool,
  /** Specify a callback that will be called when a user clicks mask, close button or Cancel button */
  onClose: PropTypes.func.isRequired,
  /** The title for Drawer */
  title: PropTypes.string,
  /** 	Width of the Drawer dialog */
  width: PropTypes.string.isRequired,
  /** Placement is top or bottom, height of the Drawer dialog	 */
  height: PropTypes.string.isRequired,
  /** preset size of drawer, default 378px and large 736px */
  size: PropTypes.string,
  /** Style of the drawer footer part. */
  footerStyle: PropTypes.shape({
    display: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  })
};

Drawer.defaultProps = {
  open: false,
  placement: "right",
  extra: null,
  size: "default",
  getContainer: () => document.body,
  centered: true,
  closeOnOutsideClick: false,
  closeOnEscape: true,
  destroyOnClose: true
};
